import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getAssetsUrl, sortDietIcons, setDefaultUser } from "../../../utils/functions";
import Modal from "react-modal";
import CryptoTool from "../../../utils/CryptoTool";
import API from "../../../utils/API";
import ErrorMessage from "./../../atoms/ErrorMessage";
import ModalSlider from "../../molecules/ModalSlider";
import Button from "../../atoms/Button";
import "keen-slider/keen-slider.min.css";
import { ReactComponent as Close } from "../../atoms/icons/close-modal.svg";

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  border: none;
  background: none;
  padding: 0;
  outline: none;
  cursor: pointer;
  user-select: none;
  outline: none;
  box-shadow: none;

  &:focus,
  &:hover,
  &:active {
    outline: none;
    box-shadow: none;
  }

  svg path {
    fill: var(--color-primary);
  }
`;

const ModalInner = styled.div`
  @media (min-width: 990px) {
    width: 752px;
  }
`;

const ModalHeading = styled.div`
  padding: 20px 50px 16px 20px;

  @media (min-width: 990px) {
    padding: 20px 30px 16px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.22;
  letter-spacing: -0.6px;
`;

const ModalTags = styled.div``;

const ModalButtonWrap = styled.div`
  padding-left: 25px;
  padding-bottom: 25px;

  @media (min-width: 990px) {
    padding-left: 40px;
  }
`;

const ModalDescription = styled.div`
  position: relative;
  padding: 32px 20px;

  > *:first-child {
    margin-top: 0;
  }

  h2 {
    margin-top: 20px;
    margin-bottom: 16px;
    font-size: 20px;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.2;
    letter-spacing: -0.6px;
  }

  p {
    margin: 15px 0;
    font-size: 16px;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.38;
    letter-spacing: 0.3px;
  }

  table {
    border-collapse: collapse;

    tr {
      td:first-child {
        text-align: left;
      }

      td:last-child {
        text-align: right;
      }
    }

    tbody td {
      padding: 4px 0;
      border: none;
      border-bottom: 1px solid #e6e6e6;
    }

    tbody td,
    tbody td p {
      font-size: 16px;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.38;
      letter-spacing: 0.3px;
    }

    tbody td p {
      margin: 0;
    }
  }

  img {
    max-width: 100%;
  }

  @media (min-width: 990px) {
    padding: 25px 30px;
    padding-right: calc(50% + 35px);
    aside,
    table {
      position: absolute;
      top: 32px;
      right: 40px;
      width: calc(50% - 50px);

      img {
        max-width: 100%;
      }
    }
  }
`;

const Icons = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
  margin-bottom: 5px;

  img {
    width: 24px;
    margin: 0 5px;

    &:first-child {
      margin-left: 0;
    }
  }
`;

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    padding: "0",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "3px",
    width: "80%",
    maxWidth: "752px",
    maxHeight: "80%",
    boxShadow: "none",
    overflowX: "hidden",
  },
  overlay: { zIndex: 1000, backgroundColor: "rgba(0, 0, 0, 0.6)" },
  button: {
    background: "none",
  },
};

Modal.setAppElement("#meal-constructor");

const shopUrl = CryptoTool.getShopUrl();
const DefaultUser = CryptoTool.getDefaultUser();
const processImageUrl = (img) => {
  if (!img) return '';

  // If img is an object with src property
  if (typeof img === 'object' && img.src) {
    const imgSrc = img.src;
    if (typeof imgSrc === 'string') {
      return imgSrc.startsWith('//') ? 'https:' + imgSrc : imgSrc;
    }
    return '';
  }

  // If img is a string
  if (typeof img === 'string') {
    return img.startsWith('//') ? 'https:' + img : img;
  }

  return '';
};

function MealModal(props) {
  const { linkToCollection, mealsCollectionButtonLabel, modalIsOpen, productId, closeModal, productData,
    title: propsTitle,
    images: propsImages } = props;

  const [title, setTitle] = useState("");
  const [tags, setTags] = useState([]);
  const [bodyHtml, setBodyHtml] = useState("");
  const [images, setImages] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const handleCloseModal = () => {
    closeModal();
  };

  const closeErrorModal = () => {
    setErrorMessage("");
    closeModal();
  };

  const fetchShopifyProduct = async (shopifyProductId) => {
    if (!shopifyProductId || shopifyProductId === 'undefined') {
      setBodyHtml("");
      const fallbackImages = (productData?.images || propsImages || [])
        .map(processImageUrl)
        .filter(url => url !== '');
      setImages(fallbackImages);
      return;
    }

    try {
      setDefaultUser();
      const { email } = window.shopifyUser;
      const url = CryptoTool.cryptUrl(shopUrl + "/api/products/" + shopifyProductId, email);

      const mealData = await API.get(url);
      setBodyHtml(mealData.data.bodyHtml || "");

      let imagesToSet = [];
      if (mealData.data.images && mealData.data.images.length > 0) {
        imagesToSet = mealData.data.images
          .map(processImageUrl)
          .filter(url => url !== '');
      } else if (productData?.images || propsImages) {
        imagesToSet = (productData.images || propsImages || [])
          .map(processImageUrl)
          .filter(url => url !== '');
      }

      setImages(imagesToSet);

    } catch (error) {
      setBodyHtml("");

      const fallbackImages = (productData?.images || propsImages || [])
        .map(processImageUrl)
        .filter(url => url !== '');
      setImages(fallbackImages);
    }
  };

  const iconsUrls = function () {
    let icons = [];
    let sortedIcons = [];
    const storeUrl = getAssetsUrl();

    tags.forEach(function (tag) {
      if (!tag || typeof tag !== 'string') {
        return;
      }
      const tagName = tag.toLowerCase();
      let fileName = tagName.split("_");

      if (tagName.includes("icon")) {
        if (fileName.length > 1) {
          icons.push({
            url: storeUrl + fileName[1].replace(" ", "-") + ".svg?v=1",
            title: tag.split("_")[1],
            tag: tag,
          });
        }
      }
    });

    sortedIcons = sortDietIcons(icons);

    return (
      <Icons className="meal-item__icons">
        {sortedIcons.map((icon, index) => {
          return <img src={icon.url} loading="lazy" decoding="async" alt={icon.title} key={index} />;
        })}
      </Icons>
    );
  };

  useEffect(
    function () {
      if (productData) {
        setTitle(productData.title || propsTitle || "");
        setTags(productData.tags || []);

        if (!modalIsOpen) {
          return;
        }

        if (!productData.body_html && !productData.bodyHtml && !productData.description) {
          const shopifyProductId = productData.shopify_product_id ||
            productData.variants?.[0]?.shopify_product_id ||
            productId;

          if (shopifyProductId && shopifyProductId !== 'undefined') {
            fetchShopifyProduct(shopifyProductId);
          } else {
            setBodyHtml("");
            const fallbackImages = (productData.images || propsImages || [])
              .map(processImageUrl)
              .filter(url => url !== '');
            setImages(fallbackImages);
          }
        } else {
          setBodyHtml(productData.body_html || productData.bodyHtml || productData.description || "");

          const processedImages = (productData.images || propsImages || [])
            .map(processImageUrl)
            .filter(url => url !== '');
          setImages(processedImages);
        }

        return;
      }

      if (!productId || productId === 'undefined') {
        return;
      }

      if (!modalIsOpen) {
        return;
      }

      const mealDataQuery = () => {
        setDefaultUser();
        const { email } = window.shopifyUser;
        return API.get(CryptoTool.cryptUrl(shopUrl + "/api/products/" + productId, email));
      };

      const getMealData = async function () {
        try {
          const mealData = await mealDataQuery();
          setTitle(mealData.data.title);
          setTags(mealData.data.tags);
          setBodyHtml(mealData.data.bodyHtml);
          setImages(mealData.data.images);
        } catch (error) {
          setErrorMessage("Network error. Please, refresh the page.");
        }
      };

      getMealData();
    },
    [modalIsOpen, productId, productData, propsTitle, propsImages]
  );

  return (
    <>
      {errorMessage.length > 0 && (
        <ErrorMessage modalIsOpen={errorMessage.length ? true : false} closeErrorModal={closeErrorModal} message={errorMessage} />
      )}
      {errorMessage.length === 0 && modalIsOpen && (title.length > 0 || productData) && (
        <Modal isOpen={modalIsOpen} style={customStyles} onRequestClose={handleCloseModal}>
          <ModalInner>
            <ModalHeading>
              {(title.length > 0 || propsTitle) && <ModalTitle>{title || propsTitle}</ModalTitle>}
              {tags.length > 0 && <ModalTags>{iconsUrls()}</ModalTags>}
            </ModalHeading>
            {images.length > 0 && (
              <>
                <ModalSlider images={images}></ModalSlider>
              </>
            )}
            {bodyHtml.length > 0 && (
              <ModalDescription
                dangerouslySetInnerHTML={{
                  __html: bodyHtml,
                }}
              ></ModalDescription>
            )}
            {bodyHtml.length === 0 && (
              <ModalDescription>
                <p>No ingredient information available for this product.</p>
              </ModalDescription>
            )}
          </ModalInner>

          {linkToCollection && mealsCollectionButtonLabel && (
            <ModalButtonWrap>
              <Button href={linkToCollection} size="small">{mealsCollectionButtonLabel}</Button>
            </ModalButtonWrap>
          )}

          <CloseButton onClick={handleCloseModal}>
            <Close />
          </CloseButton>
        </Modal>
      )}
    </>
  );
}

export default MealModal; 